export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * The message to show a user for a failed request.
 *
 * The API answers every domain failure with `{ error: "<a sentence for a
 * human>" }` (src/platform/http/errors.ts), and `ApiError.message` is that
 * sentence — so the default is to show it. `fallback` covers a network error or
 * anything that isn't an ApiError. `overrides` lets a caller reword one status
 * where the page has more context than the server does.
 *
 * Four call sites each rolled their own version of this, and only two of them
 * remembered to say something useful about a 409.
 */
export function describeApiError(
  err: unknown,
  fallback: string,
  overrides?: Record<number, string>,
): string {
  if (!(err instanceof ApiError)) return fallback;
  return overrides?.[err.status] ?? err.message ?? fallback;
}

async function handle<T>(res: Response): Promise<T> {
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    const message = (body && (body.error as string)) || res.statusText || "Request failed";
    throw new ApiError(res.status, message, body?.details);
  }
  return body as T;
}

const base = "/api";

export const api = {
  get: <T>(path: string) => fetch(`${base}${path}`, { credentials: "include" }).then(handle<T>),

  /** A response that is not JSON — a CSV export read for on-screen preview.
   *  Errors still arrive as JSON, so they go through the same unwrapping. */
  getText: async (path: string): Promise<string> => {
    const res = await fetch(`${base}${path}`, { credentials: "include" });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(
        res.status,
        (body && (body.error as string)) || res.statusText || "Request failed",
      );
    }
    return res.text();
  },

  post: <T>(path: string, data?: unknown) =>
    fetch(`${base}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: data === undefined ? undefined : JSON.stringify(data),
    }).then(handle<T>),

  patch: <T>(path: string, data: unknown) =>
    fetch(`${base}${path}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(data),
    }).then(handle<T>),

  put: <T>(path: string, data: unknown) =>
    fetch(`${base}${path}`, {
      method: "PUT",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(data),
    }).then(handle<T>),

  del: <T>(path: string) =>
    fetch(`${base}${path}`, { method: "DELETE", credentials: "include" }).then(handle<T>),

  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return fetch(`${base}${path}`, {
      method: "POST",
      credentials: "include",
      body: form,
    }).then(handle<T>);
  },
};
